import { useEffect, useRef } from "react";
import { motion, useMotionValue } from "framer-motion";

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const CHAPTERS = [
  {
    n: "01",
    title: "La Selezione dei Marchi",
    text: "Distribuiamo marchi forti come Wrangler, Freddy e Nautica: lotti pronti a box, assortimenti taglie chiari e listini trasparenti per il tuo punto vendita.",
  },
  {
    n: "02",
    title: "Logistica Globale",
    text: "Magazzino strategico nel Mediterraneo: spedizione rapida in Europa e nel mondo, con tracciamento completo di ogni spedizione B2B.",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const shutterY = useMotionValue("0%");
  const contentY = useMotionValue(70);
  const contentOpacity = useMotionValue(0.25);
  const hintOpacity = useMotionValue(1);

  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const p = total > 0 ? clamp01(-rect.top / total) : 0;
      const e = easeInOut(p);
      shutterY.set(`${-e * 102}%`);
      contentY.set(lerp(70, 0, e));
      contentOpacity.set(lerp(0.25, 1, Math.min(1, e * 1.6)));
      hintOpacity.set(p > 0.06 ? 0 : 1);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [shutterY, contentY, contentOpacity, hintOpacity]);

  return (
    <section ref={ref} className="relative h-[280vh]" data-testid="manifesto-section">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden bg-paper">
        {/* Chapters revealed beneath the shutter */}
        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="mx-auto w-full max-w-7xl px-5 lg:px-8"
        >
          <p className="mb-10 font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta">
            Il manifesto
          </p>
          <div>
            {CHAPTERS.map((c) => (
              <div
                key={c.n}
                className="grid grid-cols-1 items-baseline gap-3 border-t border-black/10 py-6 md:grid-cols-12 md:gap-10 lg:py-8"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <div className="md:col-span-3">
                  <span className="font-display text-5xl font-extrabold tracking-tighter text-ink/10 lg:text-7xl">
                    {c.n}
                  </span>
                </div>
                <div className="md:col-span-4">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-ink lg:text-4xl">
                    {c.title}
                  </h2>
                </div>
                <div className="md:col-span-5">
                  <p className="max-w-md text-sm leading-relaxed text-ink/60">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10" />
        </motion.div>

        {/* Blue garage shutter */}
        <motion.div
          style={{ y: shutterY }}
          className="absolute inset-0 z-20"
          data-testid="manifesto-shutter"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "repeating-linear-gradient(180deg, #0B54E8 0px, #0047FF 10px, #0040DD 22px, #0B54E8 32px, #003ACB 33px, #0047FF 34px)",
              boxShadow: "inset 0 -60px 80px rgba(0,20,80,0.35)",
            }}
          />
          {/* Slat highlight lines */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "repeating-linear-gradient(180deg, rgba(255,255,255,0.14) 0px, transparent 2px, transparent 34px)",
            }}
          />
          {/* Branding on shutter */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
            <span className="font-display text-4xl font-extrabold tracking-tighter text-white/95 sm:text-6xl">
              iogama<span className="text-white/50">.</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.34em] text-white/60 sm:text-[11px]">
              Il manifesto — serranda d'officina
            </span>
          </div>
          {/* Bottom roll bar */}
          <div className="absolute bottom-0 left-0 right-0">
            <div className="mx-auto flex h-9 items-center justify-center">
              <motion.span
                style={{ opacity: hintOpacity }}
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80"
                data-testid="manifesto-shutter-hint"
              >
                Scorri per alzare la serranda
              </motion.span>
            </div>
            <div
              className="h-7 w-full"
              style={{
                background: "linear-gradient(180deg, #0a2f7a 0%, #062056 45%, #0a2f7a 100%)",
                borderRadius: "9999px 9999px 0 0",
                boxShadow: "0 18px 45px rgba(6,25,70,0.55)",
              }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
